// CDV-023/CDV-024: render a `form` block; on submit, post a structured
// chat message back through the active conversation. Forms NEVER call
// an API directly — the submission is a normal user turn into the chat
// stream and the assistant decides what to do with the values (reply
// in text, paint another sala, or invoke an MCP tool with its own
// confirm/⟦PW⟧ handshake).

import { useChatConversationContext } from '@/components/chat/useChatConversation'
import { useId, useState } from 'react'
import { t } from '@/lib/i18n'

interface FormFieldOption {
  value: string
  label: string
}

interface FormField {
  name: string
  label: string
  type: 'text' | 'textarea' | 'number' | 'date' | 'select'
  required?: boolean
  placeholder?: string
  options?: FormFieldOption[]
  default?: string | number
}

interface Props { //@usedby:ComposedBody.tsx
  id: string
  title?: string
  description?: string
  submit_label?: string
  fields: FormField[]
}

export function FormBlockView({ id, title, description, submit_label, fields }: Props) {
  const { sendMessage } = useChatConversationContext()
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const groupId = useId()

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (submitted) return
    const data = new FormData(e.currentTarget)
    const values: Record<string, string | number> = {}
    const nextErrors: Record<string, string> = {}

    for (const field of fields) {
      const raw = data.get(field.name)
      const text = typeof raw === 'string' ? raw.trim() : ''
      if (field.required && !text) {
        nextErrors[field.name] = t('Required')
        continue
      }
      if (!text) continue  // optional + empty: omit from payload
      if (field.type === 'number') {
        const n = Number(text)
        if (!Number.isFinite(n)) {
          nextErrors[field.name] = t('Must be a number')
          continue
        }
        values[field.name] = n
      } else if (field.type === 'date') {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
          nextErrors[field.name] = t('Must be YYYY-MM-DD')
          continue
        }
        values[field.name] = text
      } else {
        values[field.name] = text
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }
    setErrors({})
    setSubmitted(true)
    void sendMessage(buildFormSubmitMessage(id, values))
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-border bg-card p-4 space-y-4"
      aria-labelledby={`${groupId}-title`}
    >
      {title ? (
        <div id={`${groupId}-title`} className="text-base font-semibold text-foreground">
          {title}
        </div>
      ) : (
        <span id={`${groupId}-title`} className="sr-only">
          {t('Form')}
        </span>
      )}
      {description ? (
        <div className="text-sm text-foreground-muted">{description}</div>
      ) : null}

      <div className="space-y-3">
        {fields.map((field) => (
          <FieldRow key={field.name} field={field} disabled={submitted} error={errors[field.name]} />
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {submitted ? (
          <span className="text-xs text-foreground-muted">
            {t('Submitted — the assistant will respond shortly.')}
          </span>
        ) : (
          <span />
        )}
        <button
          type="submit"
          disabled={submitted}
          className="bg-primary hover:bg-primary-hover text-primary-foreground rounded-lg px-3 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submit_label ?? t('Submit')}
        </button>
      </div>
    </form>
  )
}

function FieldRow({
  field,
  disabled,
  error,
}: {
  field: FormField
  disabled: boolean
  error?: string
}) {
  const inputId = useId()
  const baseClass =
    'w-full bg-background text-foreground placeholder:text-foreground-muted border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent disabled:opacity-60'

  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="block text-xs font-medium text-foreground">
        {field.label}
        {field.required ? <span className="text-error ml-1">*</span> : null}
      </label>
      {field.type === 'textarea' ? (
        <textarea
          id={inputId}
          name={field.name}
          rows={3}
          required={field.required}
          placeholder={field.placeholder}
          defaultValue={asString(field.default)}
          disabled={disabled}
          className={`${baseClass} resize-y`}
        />
      ) : field.type === 'select' ? (
        <select
          id={inputId}
          name={field.name}
          required={field.required}
          defaultValue={asString(field.default) ?? ''}
          disabled={disabled}
          className={baseClass}
        >
          {!field.required && <option value="">—</option>}
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={inputId}
          name={field.name}
          type={inputType(field.type)}
          required={field.required}
          placeholder={field.placeholder}
          defaultValue={asString(field.default)}
          disabled={disabled}
          className={baseClass}
        />
      )}
      {error ? <div className="text-xs text-error">{error}</div> : null}
    </div>
  )
}

function inputType(t: FormField['type']): string {
  switch (t) {
    case 'number':
      return 'number'
    case 'date':
      return 'date'
    default:
      return 'text'
  }
}

function asString(v: string | number | undefined): string | undefined {
  if (v === undefined) return undefined
  return String(v)
}

// Exported for unit tests; the chatbot's system prompt teaches the LLM
// to recognise this exact prefix (TECHNICAL.md §4.13.6).
export function buildFormSubmitMessage( //@usedby:FormBlockView.test.tsx
  formId: string,
  values: Record<string, string | number>,
): string {
  const payload = JSON.stringify({ form_id: formId, values })
  return [
    `[FORM_SUBMIT id="${formId}"]`,
    payload,
    '[/FORM_SUBMIT]',
    '',
    `(Submitted form "${formId}".)`,
  ].join('\n')
}
